import { useRef, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Routes } from 'config';
import { useLocalStorageListener } from 'hooks';
import { CourseContextProvider } from 'context';
import { useDetailsQuery } from 'store/api/auth';
import { useActiveQuery } from 'store/api/courses';
import { TranslationContextProvider } from 'context';
import { useLastQuery } from 'store/api/course-submissions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { Details, PrivateLayout as Layout } from 'components';
import { mapQueries, joinStrings, getHasChildrenSetupCompleted } from 'utils';
import { useListQuery as useCourseStepsListQuery } from 'store/api/course-steps';
import { useListQuery as useSubmissionsListQuery } from 'store/api/course-step-submissions';
import { useDetailsQuery as useCourseEnrollmentsDetailsQuery } from 'store/api/course-enrollments';
import {
  logout,
  updateAuthUser,
  selectAuthUser,
  selectIsLoggedIn,
  selectIsGuestUser,
} from 'store/slices/auth';
import {
  User,
  Course,
  CourseStep,
  CourseSubmission,
  CourseEnrollment,
  PaginationResponse,
  CourseStepSubmission,
} from 'types';

const {
  Profile,
  Auth: { Login },
  FamilyInformation: { Home, Edit, Create },
} = Routes;

export type Queries = {
  data?: User;
  course?: Course;
  enrollment?: CourseEnrollment;
  submission?: CourseSubmission;
  steps?: PaginationResponse<CourseStep>;
  submissions?: PaginationResponse<CourseStepSubmission>;
};

const familyCreatePath = joinStrings([Home, Create], '/');
const familyEditPath = joinStrings([Home, Edit], '/');
const allowedFamilyPaths = [
  Home,
  Profile.Home,
  familyEditPath,
  familyCreatePath,
];

export default function PrivateLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();

  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const authUser = useAppSelector(selectAuthUser);
  const isGuestUser = useAppSelector(selectIsGuestUser);
  const profileQuery = useDetailsQuery();
  // Use fresh query data before the effect below synchronizes the auth slice.
  const user = profileQuery.data ?? authUser;

  const {
    tenant,
    childrenCount,
    isProfileFormSubmitted,
    childrenSetupRequiredCount,
  } = user ?? {};

  const skipCourseData = !isProfileFormSubmitted && !isGuestUser;

  const courseQuery = useActiveQuery(undefined, { skip: skipCourseData });
  const courseId = courseQuery.data?.id;
  const { error } = courseQuery;
  const isCourseNotExist =
    courseQuery.isError && !!error && 'status' in error && error.status === 404;

  const { data, steps, course, submission, enrollment, submissions, ...props } =
    mapQueries<Queries>(
      {
        data: profileQuery,
        submission: useLastQuery(undefined, {
          skip: skipCourseData || isCourseNotExist,
        }),
        course: {
          ...courseQuery,
          isError: courseQuery.isError && !isCourseNotExist,
        },
        submissions: useSubmissionsListQuery(
          { limit: 100 },
          { skip: skipCourseData || isCourseNotExist }
        ),
        enrollment: useCourseEnrollmentsDetailsQuery(
          { id: courseId! },
          { skip: !courseId || skipCourseData || isCourseNotExist }
        ),
        steps: useCourseStepsListQuery(
          { limit: 100, courseId: courseId ?? '' },
          { skip: !courseId || skipCourseData || isCourseNotExist }
        ),
      },
      { initialLoadOnly: true }
    );

  const isInitialLoading =
    !props.isError &&
    (props.isLoading ||
      (!skipCourseData && !isCourseNotExist && !(course && steps)));
  const isCourseDataLoaded =
    !skipCourseData && !isInitialLoading && !props.isError;

  const { currentValue: token } = useLocalStorageListener('accessToken');
  const isTokenExistedRef = useRef<boolean>(Boolean(token));

  const isAdditionalInfo = pathname.includes(Routes.AdditionalInfo.Home);

  const hasChildren = childrenCount && childrenCount > 0;
  const hasChildrenSetupCompleted = getHasChildrenSetupCompleted(user);
  const childrenSetupRequired = childrenSetupRequiredCount !== 0;
  const isAllowedFamilyPage = allowedFamilyPaths.some((allowedPath) =>
    pathname.startsWith(allowedPath)
  );

  const logOut = useCallback(async () => {
    await dispatch(logout({ byUser: false }));
    navigate(Login, { replace: true });
  }, [dispatch, navigate]);

  useEffect(() => {
    if (data) {
      dispatch(updateAuthUser(data));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (isAllowedFamilyPage) return;

    if (!hasChildren) {
      navigate(familyCreatePath, { replace: true });
    } else if (childrenSetupRequired) {
      navigate(Home, { replace: true });
    }
  }, [hasChildren, childrenSetupRequired, isAllowedFamilyPage, navigate]);

  // useEffect(() => {
  //   if (!isProfileFormSubmitted && !isAdditionalInfo && !isGuestUser) {
  //     navigate(Routes.AdditionalInfo.Home);
  //   } else if ((isProfileFormSubmitted || isGuestUser) && isAdditionalInfo) {
  //     navigate(Routes.Dashboard.Home);
  //   }
  // }, [navigate, isGuestUser, isProfileFormSubmitted, isAdditionalInfo]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate(Login, { replace: true });
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    const wasTokenPresent = isTokenExistedRef.current;
    const tokenDisappeared = wasTokenPresent && !token;

    if (tokenDisappeared) {
      logOut();
    }

    isTokenExistedRef.current = Boolean(token);
  }, [token, logOut]);

  if (!props.isLoading && !hasChildrenSetupCompleted && !isAllowedFamilyPage) {
    return <Layout logoUrl={tenant?.siteLogo} />;
  }

  if (isAdditionalInfo) {
    return <Outlet />;
  }

  return (
    <Details {...props} loaderFullScreen isLoading={isInitialLoading}>
      <TranslationContextProvider course={course}>
        <CourseContextProvider
          course={course}
          steps={steps?.data}
          submission={submission}
          enrollment={enrollment}
          isLoading={isInitialLoading}
          isLoaded={isCourseDataLoaded}
          submissions={submissions?.data}
        >
          <Layout logoUrl={tenant?.siteLogo}>
            <Outlet />
          </Layout>
        </CourseContextProvider>
      </TranslationContextProvider>
    </Details>
  );
}
